import { useFormik, FormikHelpers } from "formik";
import { useTranslation } from "react-i18next";
import * as Yup from "yup";
import moment from "moment";
import { formValidation } from "../translation/strings";

export interface CheckoutFields {
  billing: {
    firstName: string;
    lastName: string;
    address1: string;
    city: string;
    country?: string;
    phone: string;
  };
  shipping: {
    firstName?: string;
    lastName?: string;
    address1?: string;
    city?: string;
    country?: string;
  };

  createAccount?: boolean;
  email: string;
  password?: string;
  passwordConfirm?: string;
  transferDate: string;
  transportType: string;
  billingSameAsShipping: boolean;
  comment: string;
  termsAndConditions: boolean;
  paymentMethod: string;
}

interface UseCheckoutFormOptions {
  enableReinitialize?: boolean;
  initialValues: CheckoutFields;
  onSubmit: (
    values: CheckoutFields,
    formikHelpers: FormikHelpers<CheckoutFields>
  ) => Promise<any>;
}

export const useCheckoutFormik = (options: UseCheckoutFormOptions) => {
  const { t } = useTranslation();

  const CheckoutSchema = Yup.object().shape(
    {
      billing: Yup.object().shape({
        firstName: Yup.string().required(t(formValidation.firstName)),
        lastName: Yup.string().required(t(formValidation.lastName)),
        city: Yup.string().required(t(formValidation.city)),
        address1: Yup.string().required(t(formValidation.address)),
        phone: Yup.string().required(t(formValidation.phone)),
      }),
      shipping: Yup.object().when("billingSameAsShipping", {
        is: false,
        then: Yup.object().shape({
          firstName: Yup.string().required(t(formValidation.firstName)),
          lastName: Yup.string().required(t(formValidation.lastName)),
          city: Yup.string().required(t(formValidation.city)),
          address1: Yup.string().required(t(formValidation.address)),
        }),
      }),
      transferDate: Yup.string()
        .required("Data e transportit është e nevojshme!")
        .typeError("Invalid delivery date format")
        .test(
          "date",
          "Data e transportit nuk mundë të jetë më e vogël se data e sotit!",
          function (value) {
            const todayDate = moment(new Date()).format("MM/DD/yyyy");
            const deliveryDate = moment(new Date(value!)).format("MM/DD/yyyy");

            return deliveryDate >= todayDate;
          }
        ),
      transportType: Yup.string().required(
        "Mënyra e transportit është e nevojshme!"
      ),
      billingSameAsShipping: Yup.boolean(),
      password: Yup.string().when("createAccount", {
        is: true,
        then: Yup.string().required(t(formValidation.password)),
      }),
      passwordConfirm: Yup.string()
        .when("createAccount", {
          is: true,
          then: Yup.string().required(t(formValidation.password)),
        })
        .oneOf([Yup.ref("password")], t(formValidation.passwordNotMatch)),
      email: Yup.string().required(t(formValidation.email)),
      createAccount: Yup.boolean(),
      comment: Yup.string(),
      paymentMethod: Yup.string().required(t(formValidation.paymentMethod)),
      termsAndConditions: Yup.bool().oneOf(
        [true],
        t(formValidation.termsAndConditions)
      ),
    },
    [
      ["billingSameAsShipping", "shipping"],
      ["createAccount", "password"],
    ]
  );

  return useFormik({
    enableReinitialize: true,
    initialValues: {
      ...options.initialValues,
    },
    validateOnBlur: true,
    validateOnChange: true,
    validationSchema: CheckoutSchema,
    onSubmit: async (values, formikHelpers) => {
      await options.onSubmit(values, formikHelpers);
    },
  });
};

export type CheckoutFormik = ReturnType<typeof useCheckoutFormik>;
